import { Inject, Injectable } from '@angular/core';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  // of
} from 'rxjs';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { SONG_API_SERVICE, SongApiService } from 'src/app/song/types';
import { PlaylistItemsResponse } from '../../../../../src/app/shared/projects/projects.model';

import { cloneDeep } from 'lodash-es';
import { SONGS_IN_PROJECT_PAGE_SIZE } from '../../../../../src/app/shared/projects/constants';
import { ApiService } from '../../../../../src/app/shared/services/api.service';
import { AttachmentsRepository } from 'src/app/shared/attachments/state/attachments.repository';
import { Attachment } from 'src/app/shared/attachments/models/attachment.model';
import { normalizeAttachment } from '../../../../../src/app/shared/projects/projects-api.service';
import { getPitchData, getSongs } from './debug-data/debug-data';

const DEBUG_MODE = false;

interface PlaylistResponse {
  id?: number;
  hashId: string;
  playlist: {
    description?: string;
    items: any[];
  };
  expired: boolean;
  name: string;
  brief: string;
  clientNote?: string | null;
  expectedExpirationDate: string;
  lastEditDate: string;
  createdBy: string;
  createdTime: '2019-08-14T09:52:20.168695Z';
  attachments?: Attachment[];
}

@Injectable({
  providedIn: 'root',
})
export class PlaylistApiService {
  constructor(
    @Inject('ApiService') private api: ApiService,
    private routerQuery: RouterQuery,
    @Inject(SONG_API_SERVICE) private songsService: SongApiService,
    private attachmentsRepository: AttachmentsRepository
  ) {}

  pitchId$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  pitchHasAttachments$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  pitchHash$: Observable<string | null> = this.routerQuery
    .select(state => state)
    .pipe(
      map(route => {
        if (!route.state) {
          return null;
        }
        const { params } = route.state;

        const { hash = null } = params;
        return hash;
      }),
      filter(val => Boolean(val))
    );

  get(id, queryParams: any = {}): Observable<PlaylistResponse> {
    return this.api.get<PlaylistResponse>(`pitch/${id}`, queryParams).pipe(tap(() => this.songsService.setPitchID(id)));
  }

  getSongs(id, params): Observable<PlaylistItemsResponse> {
    return this.api.get<PlaylistItemsResponse>(`pitch/${id}/playlist/items`, {
      params,
    });
  }

  post(id, queryParams: any = {}): Observable<any> {
    return this.api.post(`pitch/${id}/tracking/playback`, queryParams);
  }

  fetchAll(queryParams: any = {}): Observable<PlaylistResponse | Record<string, never> | null> {
    return this.pitchHash$.pipe(
      switchMap(id => {
        return DEBUG_MODE
          ? forkJoin([of(getPitchData()).pipe(tap(() => this.songsService.setPitchID(id))), of(getSongs())])
          : forkJoin([
              this.get(id, queryParams),
              this.getSongs(id, {
                limit: SONGS_IN_PROJECT_PAGE_SIZE,
                offset: 0,
              }),
            ]);
      }),
      map(([pitch, songs]) => {
        const copied = cloneDeep(pitch) as any;

        if (copied.playlist) {
          copied.playlist.items = songs.content;
        }
        copied.pageable = songs.pageable;
        copied.totalElements = songs.totalElements;
        return copied;
      }),
      tap(() => this.pitchId$.next(new Date().getTime())),
      tap((pitch: PlaylistResponse) => (pitch.id = this.pitchId$.value)),
      tap((pitch: PlaylistResponse) => {
        const attachments = (pitch?.attachments || []).map(normalizeAttachment);
        this.pitchHasAttachments$.next(!!attachments.length);
        this.attachmentsRepository.forceUpdateAttachments(pitch.id, attachments);
      }),
      catchError(() => of(null))
    );
  }

  increasePlaysMetric(songId: string | number): void {
    this.pitchHash$
      .pipe(
        catchError(_ => {
          return of(null);
        })
      )
      .subscribe(pitchHash => {
        this.post(pitchHash, { id: songId })
          .pipe(
            catchError(_ => {
              return of(null);
            })
          )
          .subscribe(() => {});
      });
  }
}
